import { postData } from '../utils/ajax-utils';
import CookieManager from '../utils/cookie-manager';

declare global {
    interface DocumentEventMap {
        'open_modal': CustomEvent<{ $modal: ModalHTMLElement }>;
        'close_modal': CustomEvent<{ $modal: ModalHTMLElement }>;
        'close_modal_youtube': CustomEvent<{ $modal: ModalHTMLElement }>;
        'modal_ajax_content_loaded': CustomEvent<{ action: string, serviceId: string, $modal: ModalHTMLElement }>;
    }
}

export interface ModalHTMLElement extends HTMLElement {
    open(): Promise<ModalHTMLElement>;
    close(force?: boolean): void;
}

export type ModalType = {
    $modalsOpened: ModalHTMLElement[],
    init(): void,
    onKeyDown(e: KeyboardEvent): void,
    openModal($modal: ModalHTMLElement): Promise<ModalHTMLElement>,
    closeModal($modal?: ModalHTMLElement, force?: boolean): void,
};

const Modal: ModalType = {
    $modalsOpened: [],

    init(): void {
        this.$modalsOpened = [];
        const that = this;

        document.querySelectorAll<ModalHTMLElement>('.modal').forEach($modal => {
            $modal.open = () => this.openModal($modal);
            $modal.close = (force: boolean = false) => this.closeModal($modal, force);
        });

        document.querySelectorAll<ModalHTMLElement>('.modal.auto-open').forEach($autoOpenModal => {
            if ($autoOpenModal.dataset.cookieForSingleOpen) {
                const cookieName = $autoOpenModal.dataset.cookieForSingleOpen;
                if (!CookieManager.getCookie(cookieName)) {
                    this.openModal($autoOpenModal);
                    CookieManager.setCookie(cookieName, 'is already open');
                }
            } else {
                this.openModal($autoOpenModal);
            }
        });

        document.addEventListener('click', e => {
            const $targetEl = e.target as HTMLElement;
            if ($targetEl.classList.contains('js-modal-toggler') || $targetEl.closest('.js-modal-toggler')) { // Toggler
                e.preventDefault();

                const $target = ($targetEl.classList.contains('js-modal-toggler') ? $targetEl : $targetEl.closest<HTMLElement>('.js-modal-toggler')) as HTMLElement;
                
                const modalId = $target.dataset.modalTarget as string;
                const $modal = document.getElementById(modalId) as ModalHTMLElement;

                if ($modal) {
                    if ($modal.classList.contains('open')) {
                        that.closeModal($modal);
                    } else {
                        that.openModal($modal);
                    }
                }
            } else if ($targetEl.classList.contains('js-modal-close') || $targetEl.closest('.js-modal-close')) { // Close button
                e.preventDefault();

                const $target = ($targetEl.classList.contains('js-modal-close') ? $targetEl : $targetEl.closest('.js-modal-close')) as HTMLElement;
                
                const modalId = $target.dataset.modalTarget as string;
                const $modal = document.getElementById(modalId) as ModalHTMLElement;

                if ($modal) {
                    that.closeModal($modal);
                }
            } else if ($targetEl.classList.contains('js-modal-open') || $targetEl.closest('.js-modal-open')) { // Open button
                e.preventDefault();

                // Toggle already open modal when opened from modal
                if ($targetEl.classList.contains('js-modal-open-from-modal') && this.$modalsOpened) {
                    this.$modalsOpened.forEach($modalOpen => {
                        that.closeModal($modalOpen);
                    });
                }

                const $target = ($targetEl.classList.contains('js-modal-open') ? $targetEl : $targetEl.closest('.js-modal-open')) as HTMLElement;
                
                const modalId = $target.dataset.modalTarget as string;
                const $modal = document.getElementById(modalId) as ModalHTMLElement;

                if ($modal) {
                    that.openModal($modal);
                }
            } else if ($targetEl.classList.contains('modal') && $targetEl.classList.contains('open')) { // Overlay
                that.closeModal($targetEl as ModalHTMLElement);
            }
        });

        document.addEventListener('open_modal', e => {
            that.openModal(e.detail.$modal);
        });

        document.addEventListener('close_modal', e => {
            that.closeModal(e.detail.$modal);
        });
    },

    /**
     * Close modal when pressing Escape key
     * 
     * @param {KeyboardEvent} e 
     * 
     * @return {void}
     */
    onKeyDown(e: KeyboardEvent): void {
        if (e.key === 'Escape') {
            Modal.closeModal();
        }
    },

    /**
     * Open a modal
     * 
     * @param {ModalHTMLElement} $modal 
     * 
     * @return {Promise<ModalHTMLElement>}
     */
    openModal($modal: ModalHTMLElement): Promise<ModalHTMLElement> {
        if (this.$modalsOpened.length === 0) {
            document.addEventListener('keydown', this.onKeyDown);

            document.body.classList.add('no-scroll');
        }

        if ($modal.dataset.ajaxContent) {
            const args = JSON.parse($modal.dataset.contentArgs ?? '{}');
            args.action = $modal.dataset.ajaxContent;
            args.modal_id = $modal.id;

            let hasAutoHeight = true;
            const $modalContainer = $modal.querySelector('.modal-container') as HTMLElement;
            const containerClasses = $modalContainer.classList.toString();

            $modalContainer.setAttribute('class', 'modal-container');

            if (!$modal.classList.contains('auto-height')) {
                hasAutoHeight = false;
                $modal.classList.add('auto-height');
            }

            postData(scriptVar.ajaxUrl, args).then(response => {
                if (!hasAutoHeight) {
                    $modal.classList.remove('auto-height');
                }

                $modalContainer.setAttribute('class', containerClasses);

                const $modalContent = $modal.querySelector('.js-modal-content') as HTMLElement;
                $modalContent.innerHTML = response.content;

                // Remove attributes relative to ajax request
                $modal.removeAttribute('data-ajax-content');
                $modal.removeAttribute('data-content-args');

                // Send a custom event
                const event = new CustomEvent('modal_ajax_content_loaded', {
                    detail: {
                        action: args.action,
                        serviceId: args.service_id,
                        $modal,
                    },
                });
                document.dispatchEvent(event);
            });
        }

        $modal.style.zIndex = '999' + this.$modalsOpened.length;
        $modal.classList.add('open');

        // Lock the modal close for 1s to prevent closing too fast
        $modal.classList.add('locked');
        setTimeout(() => {
            $modal.classList.remove('locked');
        }, parseInt($modal.dataset.lockTime ?? '1000'));

        this.$modalsOpened.push($modal);

        return Promise.resolve($modal);
    },

    /**
     * Close last opened modal
     * 
     * @param {ModalHTMLElement|null} $modal 
     * @param {boolean} force
     * 
     * @return {void}
     */
    closeModal($modal?: ModalHTMLElement, force: boolean = false): void {
        const $modalToClose = $modal || this.$modalsOpened[this.$modalsOpened.length - 1];

        if ($modalToClose.classList.contains('closable')) {
            if (($modalToClose.classList.contains('locked') || $modalToClose.classList.contains('locked-force')) && !force) {
                return;
            }

            $modalToClose.classList.remove('open');
            $modalToClose.style.zIndex = '';

            this.$modalsOpened = this.$modalsOpened.filter($m => $m !== $modalToClose);

            if (this.$modalsOpened.length === 0) {
                document.removeEventListener('keydown', this.onKeyDown);

                document.body.classList.remove('no-scroll');
            }
        }

        if ($modalToClose.classList.contains('youtube-modal')) {
            document.dispatchEvent(new CustomEvent('close_modal_youtube', { detail: $modal }));
        }
    },
};

export default Modal;
